import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { PrimaryButton } from '../../shared/ui';
import { updateDriver } from '../../shared/services/userService';
import { SKN_BANKS, bankPickerValue } from '../../../../utils/banks';

const inputCls =
  'w-full px-3 py-2.5 rounded-xl bg-gray-100 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#FF6B35]/40';

// Writes drivers/{uid}: bankName, bankAccountName, bankAccountNumber,
// bankPhoneNumber — same fields as iOS EditDriverProfileView's
// "Payout Details" section. Payments are sent by bank transfer, so the
// team needs these details to pay the driver.
export default function BankDetailsSheet({ open, onClose, driverId, driver }) {
  const [bank, setBank] = useState(SKN_BANKS[0]);
  const [bankOther, setBankOther] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      const stored = driver?.bankName || '';
      setBank(bankPickerValue(stored));
      setBankOther(bankPickerValue(stored) === 'Other' ? stored : '');
      setAccountName(driver?.bankAccountName || '');
      setAccountNumber(driver?.bankAccountNumber || '');
      setPhoneNumber(driver?.bankPhoneNumber || '');
      setError('');
      setSaving(false);
    }
  }, [open, driver]);

  const isValid =
    (bank !== 'Other' || bankOther.trim()) &&
    accountName.trim() &&
    accountNumber.trim();

  async function save() {
    if (!driverId || saving || !isValid) return;
    setSaving(true);
    setError('');
    try {
      await updateDriver(driverId, {
        bankName: bank === 'Other' ? bankOther.trim() : bank,
        bankAccountName: accountName.trim(),
        bankAccountNumber: accountNumber.trim(),
        bankPhoneNumber: phoneNumber.trim(),
      });
      onClose();
    } catch (e) {
      console.error('updateDriver bank details', e);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Payout Details">
      <div className="px-4 pb-6 space-y-4">
        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
            Bank
          </span>
          <select className={inputCls} value={bank} onChange={e => setBank(e.target.value)}>
            {SKN_BANKS.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </label>

        {bank === 'Other' && (
          <label className="block">
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
              Bank Name
            </span>
            <input
              className={inputCls}
              value={bankOther}
              onChange={e => setBankOther(e.target.value)}
              placeholder="Bank or credit union name"
            />
          </label>
        )}

        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
            Account Name
          </span>
          <input
            className={inputCls}
            value={accountName}
            onChange={e => setAccountName(e.target.value)}
            placeholder="Name on the account"
          />
        </label>

        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
            Account Number
          </span>
          <input
            className={inputCls}
            value={accountNumber}
            onChange={e => setAccountNumber(e.target.value)}
            placeholder="Account number"
            inputMode="numeric"
          />
        </label>

        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
            Bank Phone Number
          </span>
          <input
            className={inputCls}
            value={phoneNumber}
            onChange={e => setPhoneNumber(e.target.value)}
            placeholder="(869) 555-1234"
            inputMode="tel"
            type="tel"
          />
        </label>

        <p className="text-xs text-gray-400">
          Payments are made outside the app by bank transfer. The team uses
          these details to pay you — check the payout calendar on the
          Earnings tab for payment dates.
        </p>

        {error && <p className="text-sm text-red-500">{error}</p>}
        <PrimaryButton onClick={save} disabled={saving || !isValid}>
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Saving…
            </span>
          ) : (
            'Save'
          )}
        </PrimaryButton>
      </div>
    </Sheet>
  );
}
